export interface IDateConstraints {
  minDate: string;
  maxDate: string;
  disabledDates: string[];
}

/** Construct a local calendar date without accepting Date's overflow normalization. */
export const createCalendarDate = (year: number, month: number, day: number, hour?: number, minute = 0): Date | null => {
  const requestedHour = hour ?? 0;
  if (![year, month, day, requestedHour, minute].every(Number.isInteger)
    || year < 1 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31
    || requestedHour < 0 || requestedHour > 23 || minute < 0 || minute > 59) return null;
  const date = new Date(0);
  // setFullYear avoids the constructor's special mapping of years 0–99 to 1900–1999.
  date.setFullYear(year, month - 1, day);
  date.setHours(requestedHour, minute, 0, 0);
  // Some zones enter daylight saving at midnight. A date-only entry still names
  // that valid calendar day; an explicitly typed nonexistent time is rejected.
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    && (hour === undefined || date.getHours() === hour && date.getMinutes() === minute) ? date : null;
};

export const parseDateFields = (text: string): [number, number, number] | null => {
  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (match) return [Number(match[1]), Number(match[2]), Number(match[3])];
  match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (match) return [Number(match[3]), Number(match[2]), Number(match[1])];
  match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  return match ? [Number(match[3]), Number(match[1]), Number(match[2])] : null;
};

/** Date-only constraints are local calendar days, never UTC-midnight instants. */
export const parseDateValue = (value: string): Date | null => {
  if (!value) return null;
  const fields = parseDateFields(value);
  if (fields) return createCalendarDate(...fields);
  const iso = /^(\d{4})-(\d{2})-(\d{2})T/.exec(value);
  if (!iso || !createCalendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]))) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const calendarDayKey = (date: Date): number =>
  date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

/** Shared by manual entry, calendar buttons and the final commit boundary. */
export const dateValidationMessage = (date: Date | null, constraints: IDateConstraints): string => {
  if (!date || Number.isNaN(date.getTime()) || date.getFullYear() < 1 || date.getFullYear() > 9999) return 'Enter a valid date (YYYY-MM-DD, DD.MM.YYYY or MM/DD/YYYY).';
  const key = calendarDayKey(date);
  for (const bound of ['minDate', 'maxDate'] as const) {
    if (!constraints[bound]) continue;
    const limit = parseDateValue(constraints[bound]);
    if (!limit) return `${bound === 'minDate' ? 'Minimum' : 'Maximum'} date is invalid.`;
    if (bound === 'minDate' ? key < calendarDayKey(limit) : key > calendarDayKey(limit)) {
      return `Choose a date on or ${bound === 'minDate' ? 'after' : 'before'} ${constraints[bound]}.`;
    }
  }
  for (const value of constraints.disabledDates ?? []) {
    const disabled = parseDateValue(value);
    if (!disabled) return 'A disabled date is invalid.';
    if (key === calendarDayKey(disabled)) return 'This date is unavailable.';
  }
  return '';
};
